import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import {
  FaFileAlt, FaGlobe, FaCertificate, FaUsers, FaBalanceScale,
  FaVideo, FaKeyboard, FaMicrophoneAlt, FaEdit, FaDesktop,
} from "react-icons/fa";
import "../styles/Services.css";

const serviceIcons = {
  1: <FaFileAlt />, 2: <FaGlobe />, 3: <FaCertificate />, 4: <FaUsers />,
  5: <FaBalanceScale />, 6: <FaVideo />, 7: <FaKeyboard />, 8: <FaMicrophoneAlt />,
  9: <FaEdit />, 10: <FaDesktop />,
};

const services = [
  { id: 1, title: "Document Translation", rate: 50 },
  { id: 2, title: "Website & Software Localization", rate: 60 },
  { id: 3, title: "Certified Translations", rate: 70 },
  { id: 4, title: "Simultaneous Interpreting", rate: 100 },
  { id: 5, title: "Consecutive Interpreting", rate: 80 },
  { id: 6, title: "Remote Interpreting", rate: 75 },
  { id: 7, title: "Transcription & Subtitling", rate: 65 },
  { id: 8, title: "Voice-over & Dubbing", rate: 90 },
  { id: 9, title: "Proofreading & Editing", rate: 55 },
  { id: 10, title: "Multilingual DTP", rate: 70 },
];

function ServiceDetails() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [selectedServices, setSelectedServices] = useState([]);
  const [loading, setLoading] = useState(false);

  if (!state?.serviceId) return <p>Service not found.</p>;

  const service = services.find(s => s.id === state.serviceId);

  const handleAddService = () => {
    if (!selectedServices.find(s => s.id === service.id)) {
      setSelectedServices([...selectedServices, { ...service, hours: 1, cost: service.rate }]);
    }
  };

  const handleChangeHours = (id, hours) => {
    setSelectedServices(selectedServices.map(s => s.id === id ? { ...s, hours, cost: s.rate * hours } : s));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      alert("Name and email are required.");
      return;
    }

    if (selectedServices.length === 0) {
      alert("Please select at least one service.");
      return;
    }

    setLoading(true);

    try {
      // 1️⃣ Insert quote
      const { data: quote, error: quoteError } = await supabase
        .from("quotes")
        .insert([{
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          message: formData.message,
          status: "pending",
          total_cost: selectedServices.reduce((sum, s) => sum + s.cost, 0),
        }])
        .select()
        .single();

      if (quoteError) throw quoteError;

      console.log("Quote created:", quote);

      // 2️⃣ Insert quote items
      const items = selectedServices.map(s => ({
        quote_id: quote.id,
        service_id: s.id,
        service_name: s.title,
        rate_per_hour: s.rate,
        hours: s.hours,
        cost: s.cost,
      }));

      const { error: itemsError } = await supabase.from("quote_items").insert(items);
      if (itemsError) throw itemsError;

      console.log("Quote items inserted:", items);

      alert("Quote submitted successfully!");
      setFormData({ name: "", email: "", phone: "", message: "" });
      setSelectedServices([]);
      navigate("/thank-you");
    } catch (error) {
      console.error("Submission error:", error);
      alert("Failed to submit quote. Check console for details.");
    }

    setLoading(false);
  };

  const totalCost = selectedServices.reduce((sum, s) => sum + s.cost, 0);

  return (
    <div className="service-details">
      <h2>{service.title}</h2>
      <p>Rate: R {service.rate}/hour</p>
      <button onClick={handleAddService} disabled={selectedServices.find(s => s.id === service.id)}>
        {selectedServices.find(s => s.id === service.id) ? "Added" : "Add Service"}
      </button>

      {selectedServices.length > 0 && (
        <div className="selected-services">
          <h3>Selected Services</h3>
          {selectedServices.map(s => (
            <div key={s.id} className="selected-service">
              <div className="service-icon">{serviceIcons[s.id]}</div>
              <p>{s.title}</p>
              <input
                type="number"
                min="0.25"
                step="0.25"
                value={s.hours}
                onChange={e => handleChangeHours(s.id, Number(e.target.value))}
              /> hours
              <p>Cost: R {s.cost.toFixed(2)}</p>
            </div>
          ))}
          <p><strong>Total:</strong> R {totalCost.toFixed(2)}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="quote-form">
        <h3>Customer Details</h3>
        <input
          type="text"
          placeholder="Full Name"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={e => setFormData({ ...formData, email: e.target.value })}
          required
        />
        <input
          type="tel"
          placeholder="Phone"
          value={formData.phone}
          onChange={e => setFormData({ ...formData, phone: e.target.value })}
        />
        <textarea
          placeholder="Message"
          value={formData.message}
          onChange={e => setFormData({ ...formData, message: e.target.value })}
        />
        <button type="submit" disabled={loading}>
          {loading ? "Submitting..." : "Request Quote"}
        </button>
      </form>
    </div>
  );
}

export default ServiceDetails;
